import StarterKit from "@tiptap/starter-kit";
import { TableKit } from "@tiptap/extension-table";
import { Placeholder } from "@tiptap/extensions";
import type { AnyExtension } from "@tiptap/react";
import type { MutableRefObject } from "react";
import { AnalysisSection, type AnalysisSectionHandlers } from "./section-node";
import { createSlashCommand, type SlashBridge } from "./slash-command";

/**
 * The editor's schema, in one place.
 *
 * Content is turned into ProseMirror JSON outside the editor (when a document is
 * first loaded, and again whenever a section comes back from the worker), and
 * that conversion has to use exactly the same extension list — a schema mismatch
 * silently drops nodes rather than erroring.
 *
 * Images are deliberately absent. Text, formatting and tables are the scope;
 * adding an image node later is additive and changes nothing here.
 */

export interface AnalysisExtensionOptions {
  handlers?: MutableRefObject<AnalysisSectionHandlers>;
  slashBridge?: MutableRefObject<SlashBridge>;
}

export const buildAnalysisExtensions = (
  options: AnalysisExtensionOptions = {}
): AnyExtension[] => {
  const extensions: AnyExtension[] = [
    // StarterKit v3 already carries bold, italic, underline, strike, code,
    // headings, lists, blockquote, horizontal rule and links — no separate
    // packages needed for any of them.
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      bulletList: { keepMarks: true },
      orderedList: { keepMarks: true },
      link: {
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { class: "text-primary underline" },
      },
    }),
    TableKit.configure({
      table: { resizable: true, allowTableNodeSelection: true },
    }),
    AnalysisSection.configure({ handlers: options.handlers }),
  ];

  if (options.slashBridge) {
    extensions.push(createSlashCommand(options.slashBridge));
  }

  extensions.push(
    Placeholder.configure({
      placeholder: "Write, or press '/' for blocks…",
    })
  );

  return extensions;
};

/**
 * The same schema without the interactive parts.
 *
 * Used for parsing HTML off the wire, where node views and the slash menu are
 * irrelevant and the handler refs do not exist.
 */
export const analysisSchemaExtensions = (): AnyExtension[] =>
  buildAnalysisExtensions();
